import { DefaultError, QueryClient, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { eramanCMSApi } from "~/Services";
export interface SocialMediaIcons {
  id: string;
  name: string;
  field_social_media_icon: string;
  field_social_media_link: string;
  field_social_media_name: string;
  field_social_media_name_ms: string;
  field_social_media_name_zh: string;
  status: string;
  publish: boolean;
}

export interface Address {
  id: string;
  name: string;
  field_address_email: string[];
  field_address_en: string;
  field_address_heading_en: string;
  field_address_heading_ms: string;
  field_address_heading_zh: string;
  field_address_ms: string;
  field_address_zh: string;
  email_icon_image: string;
  field_mobile_number: string[];
  mobile_icon_image: string;
}

export interface EramanLogo {
  id: any;
  name: string;
  field_brand_image: string;
  field_common_image: string;
}

export interface Copyrights {
  id: string;
  name: string;
  field_copyrights_en: string;
  field_copyrights_ms: string;
  field_copyrights_zh: string;
  field_copyrights_pdf_en: string;
  field_copyrights_pdf_ms: string;
  field_copyrights_pdf_zh: string;
  field_privacy_policy_text_en: string;
  field_privacy_policy_text_ms: string;
  field_privacy_policy_text_zh: string;
}

interface Contents {
  id: string;
  name: string;
  content_title_en: string[];
  content_title_ms: string[];
  content_title_zh: string[];
  field_welcome_note_en: string;
  field_welcome_note_ms: string;
  field_welcome_note_zh: string;
  field_welcome_note_url: string;
  field_awards_en: string;
  field_awards_ms: string;
  field_awards_zh: string;
  field_awards_url: string;
  field_about_us_en: string;
  field_about_us_ms: string;
  field_about_us_zh: string;
  field_about_us_url: string;
  field_news_en: string;
  field_news_ms: string;
  field_news_zh: string;
  field_news_url: string;
  field_contact_us_en: string;
  field_contact_us_ms: string;
  field_contact_us_zh: string;
  field_contact_us_url: string;
  field_career_en: string;
  field_career_ms: string;
  field_career_zh: string;
  field_career_url: string;
  published: boolean;
}

export interface AboutEraman {
  id: string;
  name: string;
  content_title_en: string[];
  content_title_ms: string[];
  content_title_zh: string[];
  field_welcome_note_en: string;
  field_welcome_note_ms: string;
  field_welcome_note_zh: string;
  field_welcome_note_url: string;
  field_awards_en: string;
  field_awards_ms: string;
  field_awards_zh: string;
  field_awards_url: string;
  field_about_us_en: string;
  field_about_us_ms: string;
  field_about_us_zh: string;
  field_about_us_url: string;
  field_news_en: string;
  field_news_ms: string;
  field_news_zh: string;
  field_news_url: string;
  field_contact_us_en: string;
  field_contact_us_ms: string;
  field_contact_us_zh: string;
  field_contact_us_url: string;
  field_career_en: string;
  field_career_ms: string;
  field_career_zh: string;
  field_career_url: string;
  published: boolean;
}

export interface EramanPrivilege {
  id: string;
  field_eraman_title_en: string;
  field_eraman_title_ms: string;
  field_eraman_title_zh: string;
  field_membership_application_en: string;
  field_membership_application_ms: string;
  field_membership_application_zh: string;
  field_term_conditions_en: string;
  field_term_conditions_ms: string;
  field_term_conditions_zh: string;
  field_terms_url: string;
  field_faq_en: string;
  field_faq_ms: string;
  field_faq_zh: string;
  field_faq_url: string;
  published: boolean;
}

export interface TravellersInfo {
  id: string;
  field_travellers_info_en: string;
  field_travellers_info_ms: string;
  field_travellers_info_zh: string;
  field_baggage_guide_en: string;
  field_baggage_guide_ms: string;
  field_baggage_guide_zh: string;
  field_baggage_guide_url: string;
  field_duty_free_en: string;
  field_duty_free_ms: string;
  field_duty_free_zh: string;
  field_duty_free_url: string;
  field_lag_restrict_en: string;
  field_lag_restrict_ms: string;
  field_lag_restrict_zh: string;
  field_lags_restrict_url: string;
  published: boolean;
}

const fetchData = async <T>(url: string): Promise<T> => {
  const response = await eramanCMSApi.get<T>(url);
  console.log("fetchData", response, url);
  return response.data;
};

export const createDataHook =
  <T>(key: string, url: string) =>
  (
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key],
        enabled: false,
        queryFn: () => fetchData<T>(url),
        refetchOnWindowFocus: false,
        ...options,
      },
      queryClient
    );
  };

export const createDataHooksForPages =
  <T>(key: string, getUrl: (listId: string) => string) =>
  (
    listId: string,
    options?: UseQueryOptions<T, DefaultError, T, QueryKey>,
    queryClient?: QueryClient
  ): UseQueryResult<T, DefaultError> => {
    return useQuery<T, DefaultError, T, QueryKey>(
      {
        queryKey: [key, listId],
        enabled: false,
        queryFn: () => fetchData<T>(getUrl(listId)),
        refetchOnWindowFocus: false,
        ...options,
      },
      queryClient
    );
  };

const useSocialMediaIcons = createDataHooksForPages<SocialMediaIcons[]>(
  "socialMediaIcons",
  (listId) => `/api/footer-section-icons-api-crud-data/get-all-fields/${listId}`
);

const useContents = createDataHooksForPages<Contents[]>(
  "contents",
  (listId) => `/api/footer-content-data-list/${listId}`
);

const useAboutEramanFooter = createDataHook<AboutEraman[]>("aboutEramanFooter", `/api/footer-content-data-list/624`);

const useEramanPrivilegeFooter = createDataHook<EramanPrivilege[]>(
  "eramanPrivilegeFooter",
  `/api/get-all-eraman-card-details`
);

const useTravellersInfoFooter = createDataHook<TravellersInfo[]>(
  "travellersInfoFooter",
  `/api/get-footer-travellers-info-details`
);

const useAddress = createDataHook<Address[]>("contactCentre", `/api/footer-address-data/all-fields`);

const useCopyrights = createDataHook<Copyrights[]>("copyrights", `/api/footer-copyrights-data/all-fields`);

const useEramanLogo = createDataHook<EramanLogo[]>("eramanLogo", `/api/companylogo/all`);

export {
  useAboutEramanFooter,
  useAddress,
  useContents,
  useCopyrights,
  useEramanLogo,
  useEramanPrivilegeFooter,
  useSocialMediaIcons,
  useTravellersInfoFooter,
};
